/**
 * Copy of useForm from @/utils/useForm:
 *  - Changed to pass controlled values state
 *  - Changed type of resetForm to allow passing reset values
 *  - Changed handleSubmit return type to accept event as optional parameter
 */

import { EMPTY_STRING } from '@/data/constants/marketing';
import { useNotifications } from '@/data/Content/Notifications';
import {
	AutocompleteChangeReason,
	AutocompleteInputChangeReason,
	SelectChangeEvent,
} from '@mui/material';
import { mapValues } from 'lodash';
import {
	ChangeEvent,
	FormEvent,
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { FormState as CoreFormState, ErrorState } from 'presentation/utils/core/useForm';

type Submit<T extends FormInput> = (values: T, event?: FormEvent<HTMLFormElement>) => void;

type FormInput = Record<string, unknown> & {
	dirty?: boolean;
};

export type FormState<T extends FormInput> = Omit<
	CoreFormState<T>,
	'resetForm' | 'handleSubmit'
> & {
	resetForm: (options?: { values?: T }) => void;
	handleSubmit: (
		onSubmit: Submit<T>,
		event?: SubmitEvent
	) => (event?: FormEvent<HTMLFormElement>) => void;
};

const focusOnFirstError = (form?: HTMLFormElement) => {
	if (!form) return;
	const length = form.elements.length;
	// eslint-disable-next-line functional/no-loop-statement
	for (let i = 0; i < length; i++) {
		const element = form.elements.item(i) as HTMLInputElement;
		const error = element?.checkValidity ? !element.checkValidity() : false;
		if (error) {
			element.focus();
			return;
		}
	}
};

const getFormFields = (form: HTMLFormElement | null): string[] => {
	const rc = [];
	if (form) {
		const length = form.elements.length;
		// eslint-disable-next-line functional/no-loop-statement
		for (let i = 0; i < length; i++) {
			const element = form.elements.item(i) as HTMLInputElement;
			if (element.name) {
				rc.push(element.name);
			}
		}
	}
	return rc;
};

export const useForm = <T extends FormInput>({
	values,
	setValues,
}: {
	values: T;
	setValues: React.Dispatch<React.SetStateAction<T>>;
}): FormState<T> => {
	const formRef = useRef<HTMLFormElement>(null);
	const initialValuesRef = useRef<T>(values);
	const [showError, setShowError] = useState(false);

	const initialError = useMemo(
		() =>
			Object.keys(initialValuesRef.current).reduce(
				(initial, current) => ({ ...initial, [current]: false }),
				{} as ErrorState<T>
			),
		[]
	);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [error, setError] = useState<ErrorState<T>>(() => initialError);
	const handleInputChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const elm = event.target;
			const { name, value, type, checked, files } = elm;
			const _name = name as keyof T;
			setValues((previousValues) => ({
				...previousValues,
				[_name]: type === 'checkbox' ? checked : type === 'file' ? { files, value } : value,
				...(previousValues.dirty !== undefined && { dirty: true }),
			}));
		},
		[setValues]
	);

	const handleSelectChange = useCallback(
		(event: SelectChangeEvent) => {
			const elm = event.target;
			const { name, value } = elm;
			const _name = name as keyof T;
			setValues((previousValues) => ({
				...previousValues,
				[_name]: value,
				...(previousValues.dirty !== undefined && { dirty: true }),
			}));
		},
		[setValues]
	);

	const handleAutoCompleteInputChange = useCallback(
		(name: keyof T) =>
			(_event: SyntheticEvent, value: string, _reason: AutocompleteInputChangeReason) => {
				setValues((previousValues) => ({
					...previousValues,
					[name]: value,
					...(previousValues.dirty !== undefined && { dirty: true }),
				}));
			},
		[setValues]
	);

	const handleAutoCompleteChange = useCallback(
		(key: keyof T) =>
			(_event: SyntheticEvent, value: T[typeof key], _reason: AutocompleteChangeReason) => {
				setValues((previousValues) => ({
					...previousValues,
					[key]: value,
					...(previousValues.dirty !== undefined && { dirty: true }),
				}));
			},
		[setValues]
	);

	const onNamedValueChange = useCallback(
		(name: keyof T, value: T[typeof name]) => {
			setValues((previousValues) => ({
				...previousValues,
				[name]: value,
				...(previousValues.dirty !== undefined && { dirty: true }),
			}));
		},
		[setValues]
	);

	const { clearMessage } = useNotifications();

	const handleSubmit = useCallback(
		(onSubmit: (currentValues: T, event?: FormEvent<HTMLFormElement>) => Promise<void> | void) =>
			async (event?: FormEvent<HTMLFormElement>) => {
				setSubmitting(true);
				try {
					clearMessage();
					event?.preventDefault();

					const trimmedValues = mapValues(values, (value: any) =>
						typeof value === 'string' ? value.trim() : value
					);

					if (values.dirty === false || formRef.current?.checkValidity()) {
						await onSubmit(trimmedValues, event);
					} else {
						focusOnFirstError(event?.currentTarget);
						setShowError(true);
					}
				} finally {
					setSubmitting(false);
				}
			},
		[values, clearMessage]
	);

	const clearForm = useCallback(() => {
		setValues((previousValues) =>
			mapValues(previousValues, (value: any) => (typeof value === 'string' ? EMPTY_STRING : value))
		);
	}, [setValues]);

	const resetForm = useCallback(
		(options?: { values?: T }) => {
			initialValuesRef.current = options?.values ?? initialValuesRef.current;
			setValues(initialValuesRef.current);
			setShowError(false);
		},
		[setValues]
	);

	const validate = useCallback(() => {
		let rc = true;
		if (!formRef.current?.checkValidity()) {
			focusOnFirstError(formRef.current ?? undefined);
			setShowError(true);
			rc = false;
		} else {
			setShowError(false);
		}
		return rc;
	}, []);

	useEffect(() => {
		if (showError) {
			const fields = getFormFields(formRef.current);
			const newError = (fields as (keyof T)[]).reduce((previousError, fieldName) => {
				let element: HTMLInputElement = formRef.current?.[fieldName as string];
				if (element instanceof RadioNodeList) {
					element = element[0] as HTMLInputElement;
				}
				previousError[fieldName] = element?.checkValidity ? !element.checkValidity() : false;
				return previousError;
			}, {} as ErrorState<T>);
			setError(newError);
		} else {
			setError(initialError);
		}
	}, [initialError, showError, values]);

	return {
		values,
		handleAutoCompleteInputChange,
		handleAutoCompleteChange,
		handleInputChange,
		handleSelectChange,
		onNamedValueChange,
		handleSubmit,
		error,
		setError,
		formRef,
		clearForm,
		submitting,
		resetForm,
		validate,
	};
};
